import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'api/v1/cms/orders';
export const getOrderCommentsEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    orderId: string;
    page: TParam;
    limit: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetOrderCommentsApi(orderId: string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = 1;
  const limit = 100;
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint + `/${orderId}/comments`, {
        params: { page, limit },
      });

      // Comments come back paginated; the screen uses the rows.
      return data.data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  // Keyed by order: one order's comments must never be served from another's cache.
  return useQueryWrapper([endpoint, { orderId, page, limit }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
